import React from 'react';
import styled from 'styled-components';
import { throttle } from 'lodash';
import { useStoreActions, useStoreState, useStore } from '../store';
import { GlobalStyles } from './GlobalStyles';
import { TopControls } from './TopControls';
import { BottomControls } from './BottomControls';
import { LanguageOptions } from './LanguageOptions';
import { SubtitleOptions } from './SubtitleOptions';
import { ModifierIndicator } from './ModifierIndicator';
import { Video } from './Video';
import { VideoFrameOverlay } from './VideoFrameOverlay';
import { ResumeOverlay } from './ResumeOverlay';
import { SubtitleOverlay } from './SubtitleOverlay';
import { ControlsOverlay } from './ControlsOverlay';
import { LoadingOverlay } from './LoadingOverlay';
import { ErrorOverlay } from './ErrorOverlay';
import { VideoPlayerProps, PlayerStatus } from '../typings';
import { ContextMenuOverlay } from './ContextMenuOverlay';
import { PlayerError } from '../errors/PlayerError';
import { ChromecastButton } from './ChromecastButton';
import { theme } from '../utils/theme';
import { useMediaQuery } from '../hooks/useMediaQuery';

const globalWindow: any = window;

export const Player = (props: VideoPlayerProps) => {
  const playerRef = React.useRef<HTMLDivElement | null>(null);
  const interactionsTimeout = React.useRef<any>(null);
  const actions = useStoreActions((actions) => actions);
  const status = useStoreState((state) => state.player.status);
  const controlsVisible = useStoreState((state) => state.player.controlsVisible);
  const isMobile = useMediaQuery(theme.mobileMediaQuery);
  const store = useStore();
  const idleTimeout = isMobile ? 3000 : 1500;

  /**
   * Load configuration from props into the store
   * and initialize the video player.
   *
   * This is done only once when the player component is mounted.
   */
  React.useEffect(() => {
    (async function load() {
      const actions = store.getActions();

      try {
        await actions.config.load(props);
        await actions.videos.fetchData();
        await actions.videos.loadWorkoutVideo();
      } catch (e) {
        const error = e as any;
        console.error(error);
        actions.error.setError(error.code ? error : new PlayerError());
      }
    })();
  }, []);

  /**
   * Load player DOM reference into the store,
   * Used for fullscreen feature.
   */
  React.useEffect(() => {
    if (!playerRef.current) return;
    actions.player.setPlayerElement(playerRef.current);
  }, [actions]);

  /**
   * Register keyboard shortcuts event listener.
   */
  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);

    function handleKeyPress(e: KeyboardEvent) {
      store.getActions().player.processKeyPress(e);
    }

    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  /**
   * Register store in global context.
   */
  React.useEffect(() => {
    globalWindow.VideoPlayerStore = store;
    globalWindow.vp = store;

    return () => {
      delete globalWindow.VideoPlayerStore;
      delete globalWindow.vp;
    };
  }, []);

  /**
   * Cleanup and destroy store on unmount.
   */
  React.useEffect(() => {
    return () => store.getActions().player.destroy();
  }, []);

  /**
   * Hide player controls when player resumes playback.
   */
  React.useEffect(() => {
    if (status === PlayerStatus.Playing) {
      clearTimeout(interactionsTimeout.current);
      interactionsTimeout.current = setTimeout(hideControls, idleTimeout);
    }
  }, [status]);

  /**
   * Show player controls when the mouse is moving.
   * Hide player controls when the mouse is idle.
   */
  const handleMouseMove = throttle(() => {
    clearTimeout(interactionsTimeout.current);
    actions.player.setPlayerControlsVisible(true);
    interactionsTimeout.current = setTimeout(() => {
      hideControls();
    }, idleTimeout);
  }, 500);

  function hideControls() {
    const state = store.getState();
    const { playbackCurrentTime, languageMenuVisible, subtitleOptionsVisible } = state.player;
    const playbackStarted = playbackCurrentTime > 0;

    if (playbackStarted && !languageMenuVisible && !subtitleOptionsVisible) {
      actions.player.setPlayerControlsVisible(false);
    }
  }

  return (
    <ResumeOverlay>
      <Container
        data-testid="playerContainer"
        ref={playerRef}
        onMouseMove={handleMouseMove}
        isControlsVisible={controlsVisible}
      >
        <ContextMenuOverlay>
          <GlobalStyles />
          <Video />
          <VideoFrameOverlay />
          <SubtitleOverlay />
          <ControlsOverlay>
            <TopControls />
            <BottomControls />
            <LanguageOptions />
            <SubtitleOptions />
          </ControlsOverlay>
          <TopRightControls>
            <ChromecastButton />
            <ModifierIndicator />
          </TopRightControls>
          <LoadingOverlay />
          <ErrorOverlay />
        </ContextMenuOverlay>
      </Container>
    </ResumeOverlay>
  );
};

export const Container = styled.div<{ isControlsVisible: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.bbBlack};
  cursor: ${({ isControlsVisible }) => (isControlsVisible ? 'auto' : 'none')};
  font-family: 'Lato', sans-serif;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TopRightControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: absolute;
  top: 32px;
  right: 32px;
  z-index: 1;

  @media ${theme.mobileMediaQuery} {
    top: 24px;
    right: 24px;
  }
`;
